import { JMCHtml } from "@components/JMCHtml/JMCHtml";
import { NotificationImpression } from "@jmc/core/src/components/NotificationImpression/NotificationImpression";
import { Typography } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";
import useJnjBranding from "@jmc/utils/hooks/useJnjBranding";
import { isEmptyHtml } from "@jmc/utils/utils/isEmptyHtml";
import { useDisclaimer } from "@utils/hooks/useDisclaimer/index";
import React, { ReactNode, useState } from "react";

import { JMCLinkModal } from "../JMCLinkModal/JMCLinkModal";

interface DisclaimerProps {
    url?: string;
    openModal: boolean;
    onClose?: () => void;
    onSubmit?: () => void;
    onCancel?: () => void;
    onClick?: () => void;
    children?: ReactNode;
    anchor_id?: string;
    disclaimerType?: "event" | "medical" | "scientific";
}

const defaultScientificText =
    'You are requesting access to Scientific content. By clicking "OK" you will have access to the content.';

const defaultMedicalText =
    'You are requesting access to a Medical Education content. The program and the content of the presentations is owned by a Scientific Third-Party Committee and the respective faculty members respectively and does not necessarily reflect the view of Janssen. All contents included in this section are for your exclusive use, not being allowed any other use or the distribution to third parties in any form of all or part of these contents. By clicking "OK" you will have access to the content.';

const defaultEventMedicalText =
    'You are requesting access to a Medical Education content. The program and the content of the presentations is owned by a Scientific Third-Party Committee and the respective faculty members respectively and does not necessarily reflect the view of Janssen. All contents included in this section are for your exclusive use, not being allowed any other use or the distribution to third parties in any form of all or part of these contents. By clicking "I Understand" you will have access to the content.';

/**
 * Presentation component for modal dialog shown when navigating to medical content.
 */
export const MedicalContentDisclaimer: React.FunctionComponent<DisclaimerProps> = ({
    children,
    openModal,
    url,
    onClose = () => null,
    onSubmit = () => null,
    onCancel = () => null,
    onClick = () => null,
    anchor_id = "",
    disclaimerType = "medical",
}: DisclaimerProps): JSX.Element => {
    const [action, setAction] = useState(null);
    const localizedDisclaimer = useDisclaimer();
    const { jnjFullBranded } = useJnjBranding();

    let title: string;
    let okButtonText: string;
    let cancelButtonText: string;
    let disclaimerText: JSX.Element;
    let notification: string;

    switch (disclaimerType) {
        case "event":
            notification = "medical event disclaimer";
            title = localizedDisclaimer?.medical_event_disclaimer?.title ?? "Medical Event Disclaimer";
            okButtonText = localizedDisclaimer?.medical_event_disclaimer?.ok_button_text ?? "I understand";
            cancelButtonText = localizedDisclaimer?.medical_event_disclaimer?.cancel_button_text ?? "Cancel";
            disclaimerText = isEmptyHtml(localizedDisclaimer?.medical_event_disclaimer?.text) ? (
                <Typography>{defaultEventMedicalText}</Typography>
            ) : (
                <div>
                    <JMCHtml useLargeText={jnjFullBranded}>
                        {localizedDisclaimer?.medical_event_disclaimer?.text}
                    </JMCHtml>
                </div>
            );
            break;
        case "medical":
            notification = "medical content disclaimer";
            title = localizedDisclaimer?.medical_content_disclaimer?.title ?? "Medical Content Disclaimer";
            okButtonText = localizedDisclaimer?.medical_content_disclaimer?.ok_button_text ?? "OK";
            cancelButtonText = localizedDisclaimer?.medical_content_disclaimer?.cancel_button_text ?? "Cancel";
            disclaimerText = isEmptyHtml(localizedDisclaimer?.medical_content_disclaimer?.text) ? (
                <Typography>{defaultMedicalText}</Typography>
            ) : (
                <div>
                    <JMCHtml useLargeText={jnjFullBranded}>
                        {localizedDisclaimer?.medical_content_disclaimer?.text}
                    </JMCHtml>
                </div>
            );
            break;
        case "scientific":
            notification = "scientific publications disclaimer";
            title = localizedDisclaimer?.scientific_publications_disclaimer?.title ?? "Scientific Content Disclaimer";
            okButtonText = localizedDisclaimer?.scientific_publications_disclaimer?.ok_button_text ?? "OK";
            cancelButtonText = localizedDisclaimer?.scientific_publications_disclaimer?.cancel_button_text ?? "Cancel";
            disclaimerText = isEmptyHtml(localizedDisclaimer?.scientific_publications_disclaimer?.text) ? (
                <Typography>{defaultScientificText}</Typography>
            ) : (
                <div>
                    <JMCHtml useLargeText={jnjFullBranded}>
                        {localizedDisclaimer?.scientific_publications_disclaimer?.text}
                    </JMCHtml>
                </div>
            );
            break;
        default:
            break;
    }

    return (
        <>
            <JMCLinkModal
                title={title}
                url={url}
                open={openModal}
                onClose={() => {
                    setAction("close");
                    setTimeout(onClose);
                }}
                onSubmit={() => {
                    setAction("ok");
                    setTimeout(onSubmit);
                }}
                onCancel={() => {
                    setAction("cancel");
                    setTimeout(onCancel);
                }}
                OKButtonText={okButtonText}
                CancelButtonText={cancelButtonText}
                anchor_id={anchor_id}
            >
                <NotificationImpression
                    name={title || notification}
                    trigger="user-action"
                    type={notification}
                    action={action}
                >
                    {disclaimerText}
                </NotificationImpression>
            </JMCLinkModal>
            {children && <span onClick={onClick}>{children}</span>}
        </>
    );
};

export default MedicalContentDisclaimer;
